import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout, Spin } from 'antd';
import HeaderCom from './components/HeaderCom';
import FooterCom from './components/FooterCom';
import ContentWrapperCom from './components/ContentWrapperCom';
import AuthProvider from './providers/AuthProvider';
import SessionErrorHandler from './components/SessionErrorHandler';
import ProtectedRoute from './components/ProtectedRoute';
import { ApiAuthProvider } from './hooks/useApiAuth';



const Home = React.lazy(() => import('./pages/Home'));
const Login = React.lazy(() => import('./pages/Login'));
const SupportDownloads = React.lazy(() => import('./pages/SupportDownloads'));
const SupportPlayground = React.lazy(() => import('./pages/SupportPlayground'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Admin = React.lazy(() => import('./pages/Admin'));
const AdminAssemble = React.lazy(() => import('./pages/AdminAssemble'));
const AdminAssembleEdit = React.lazy(() => import('./pages/AdminAssembleEdit'));
const AdminAssembleBatch = React.lazy(() => import('./pages/AdminAssembleBatch'));
const AdminAssembleUpload = React.lazy(() => import('./pages/AdminAssembleUpload'));
const AdminReservation = React.lazy(() => import('./pages/AdminReservation'));
const AdminDoc = React.lazy(() => import('./pages/AdminDoc'));
const AdminDocEdit = React.lazy(() => import('./pages/AdminDocEdit'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ApiAuthProvider>
        <SessionErrorHandler />
        <Layout style={{ minHeight: '100vh' }}>
          <HeaderCom />
          <ContentWrapperCom>
            <Suspense
              fallback={
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                  <Spin size="large" />
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/support" element={<Navigate to="/" replace />} />
                <Route
                  path="/downloads"
                  element={
                    <ProtectedRoute>
                      <SupportDownloads />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/support/downloads"
                  element={<Navigate to="/downloads" replace />}
                />
                <Route
                  path="/support/playground"
                  element={<Navigate to="/playground" replace />}
                />
                <Route
                  path="/playground"
                  element={
                    <ProtectedRoute>
                      <SupportPlayground />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requiredGroups={['/official-site/']}>
                      <Admin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/assemble"
                  element={
                    <ProtectedRoute requiredGroups={['/official-site/packager']}>
                      <AdminAssemble />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/assemble/edit"
                  element={
                    <ProtectedRoute requiredGroups={['/official-site/packager']}>
                      <AdminAssembleEdit />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/assemble/batch"
                  element={
                    <ProtectedRoute requiredGroups={['/official-site/packager']}>
                      <AdminAssembleBatch />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/assemble/upload"
                  element={
                    <ProtectedRoute requiredGroups={['/official-site/uploader']}>
                      <AdminAssembleUpload />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/reservation"
                  element={
                    <ProtectedRoute requiredGroups={['/official-site/packager']}>
                      <AdminReservation />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/doc"
                  element={
                    <ProtectedRoute requiredGroups={['/official-site/guide']}>
                      <AdminDoc />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/doc/upload"
                  element={
                    <ProtectedRoute requiredGroups={['/official-site/guide']}>
                      <AdminDocEdit />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/doc/edit/:id"
                  element={
                    <ProtectedRoute requiredGroups={['/official-site/guide']}>
                      <AdminDocEdit />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ContentWrapperCom>
          <FooterCom />
        </Layout>
      </ApiAuthProvider>
    </AuthProvider>
  );
};

export default App;
